import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { conversationKeys, useGetConversationsList } from "@ac-expo/api";
import { useWebSocket } from "./websocket.context";
import { ConversationSummary } from "@ac-expo/api-types";

interface MessageContextType {
  haveNewMessage: boolean;
  markAsRead: () => void;
  checkNewMessage: (allConversations?: ConversationSummary[]) => void;
  markConversationAsRead: (conversationId: string) => void;
}

const defaultValue = {
  haveNewMessage: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  markAsRead: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  checkNewMessage: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  markConversationAsRead: () => {},
};

type Props = {
  children: ReactNode;
};

const NotificationsContext = createContext<MessageContextType>(defaultValue);

export const useMessageContext = () => {
  return useContext(NotificationsContext);
};

export const MessageProvider = ({ children }: Props) => {
  const [haveNewMessage, setHaveNewMessage] = useState(false);
  const { subscribe, socket } = useWebSocket();
  const client = useQueryClient();
  const { data } = useGetConversationsList({
    exponent: true,
    organizer: true,
    visitor: true,
  });
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!socket) return;
    const notificationsSub = subscribe("newMessage", () => {
      console.log("new message");
      setHaveNewMessage(true);
      queryClient.invalidateQueries({
        queryKey: conversationKeys.all,
      });
    });

    return () => {
      console.log("Disconnected from socket message");
      notificationsSub();
    };
  }, [client, socket, subscribe]);

  useEffect(() => {
    checkNewMessage();
  }, [data]);

  const markAsRead = async () => {
    setHaveNewMessage(false);
  };

  const checkNewMessage = (allConversations?: ConversationSummary[]) => {
    const messages = allConversations || data;
    if (!messages || messages.length === 0) return;
    let newMessage = false;
    messages.forEach((conversation) => {
      if (!conversation.lastMessage) return;
      if (!conversation.lastMessage.isRead) {
        newMessage = true;
      }
    });
    setHaveNewMessage(newMessage);
  };

  const markConversationAsRead = (conversationId: string) => {
    const currentConversations = queryClient.getQueryData<
      ConversationSummary[]
    >(conversationKeys.list({}));

    if (currentConversations) {
      const conversation = currentConversations.find(
        (conv) => conv.with.id === conversationId
      );

      if (conversation?.lastMessage && !conversation.lastMessage.isRead) {
        const otherUnreadExists = currentConversations.some(
          (conv) =>
            conv.with.id !== conversationId &&
            conv.lastMessage &&
            !conv.lastMessage.isRead
        );

        setHaveNewMessage(otherUnreadExists);
      }
    }
  };

  return (
    <NotificationsContext.Provider
      value={{
        haveNewMessage,
        markAsRead,
        checkNewMessage,
        markConversationAsRead,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
